import Highcharts from 'highcharts';
import { SliceModel, escapeHtml } from './resultsSummary.utils';

// Venus (HC12) inherits the HC10+ default palette; this repo is on HC 9.3.3,
// whose defaults differ, so set it explicitly to match.
export const HC_PALETTE = [
	'#2caffe',
	'#544fc5',
	'#00e272',
	'#fe6a35',
	'#6b8abc',
	'#d568fb',
	'#2ee0ca',
	'#fa4b42',
	'#feb56a',
	'#91e8e1',
];
export const UNKNOWN_COLOR = '#BDBDBD';

const TITLE_STYLE = { fontSize: '0.8125rem', fontWeight: '600' };

type PiePointOptions = Highcharts.PointOptionsObject & {
	lengthMetres: number;
};

export const buildPieOptions = ({
	title,
	slices,
	formatLabel,
	tooltipLine,
}: {
	title: string;
	slices: SliceModel[];
	formatLabel: (label: string, isUnknown: boolean) => string;
	tooltipLine: (label: string, count: number, lengthMetres: number) => string;
}): Highcharts.Options => ({
	chart: { type: 'pie', height: 260 },
	credits: { enabled: false },
	colors: HC_PALETTE,
	title: { text: title, style: TITLE_STYLE },
	tooltip: {
		pointFormatter: function (this: Highcharts.Point) {
			const lengthMetres =
				(this.options as Partial<PiePointOptions>).lengthMetres ?? 0;
			return tooltipLine(this.name, this.y ?? 0, lengthMetres);
		},
	},
	plotOptions: {
		pie: {
			allowPointSelect: true,
			cursor: 'pointer',
			startAngle: -90,
			endAngle: 90,
			center: ['50%', '75%'],
			dataLabels: { enabled: true, distance: 20 },
		},
	},
	series: [
		{
			name: title,
			type: 'pie',
			data: slices.map(
				(s): PiePointOptions => ({
					// Raw DB labels — escape so they render as text, never markup.
					name: escapeHtml(formatLabel(s.label, s.isUnknown)),
					y: s.count,
					lengthMetres: s.length,
					...(s.isUnknown ? { color: UNKNOWN_COLOR } : {}),
				}),
			),
		},
	],
});

export const buildYearOptions = ({
	title,
	points,
	tooltipLine,
}: {
	title: string;
	points: [number, number][];
	tooltipLine: (year: number, count: number) => string;
}): Highcharts.Options => ({
	chart: { type: 'line', height: 260 },
	credits: { enabled: false },
	colors: HC_PALETTE,
	title: { text: title, style: TITLE_STYLE },
	legend: { enabled: false },
	// Numeric axis so years are spaced to scale (I360A-3150: categories
	// dropped empty years and distorted the distribution).
	xAxis: {
		type: 'linear',
		allowDecimals: false,
		title: { text: undefined },
	},
	yAxis: { title: { text: undefined }, allowDecimals: false },
	tooltip: {
		pointFormatter: function (this: Highcharts.Point) {
			return tooltipLine(this.x, this.y ?? 0);
		},
	},
	series: [{ name: title, type: 'line', data: points }],
});
